import { useEffect, useState } from 'react'
import { Modal, Avatar } from '@ofself/shell'
import { api } from './api.js'
import { qh } from './qh.js'

/* SHARING IS PER WORD (owner call, carried over from the app this replaced:
   "bring the sharing capability to the object level so I can share right on
   each page"). The doors a word has depend on whose it is:

   · YOUR OWN WORD — one person with a view or an edit verb, or a whole
     privacy circle, read-only. Both are top-level navigations to Paradigm's
     own consent page: the app never asks for a credential and never sees one.
     And any of your groups, as a COPY (owner call 2026-09-19).
   · A FRIEND'S WORD — theirs to share to people; yours to copy into a group.
   · A GROUP'S WORD (2026-09-19) — the group's members already read it: a
     group's dictionary is theirs together, and on this platform there is no
     pushing a word into one member's own dictionary. So the sheet says WHO
     reads it, and offers the copy out — into your own dictionary, or into
     another group of yours. */
export default function ShareSheet({ open, onClose, word, groups = [], activeGroup = null, onCopy }) {
  const [friends, setFriends] = useState(null)
  const [realms, setRealms] = useState(null)
  const [audience, setAudience] = useState(null)
  const [members, setMembers] = useState(null)
  const [access, setAccess] = useState('view')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState({})   // target → 'copied' | 'there' | 'failed'

  // a word of your own, in your own dictionary — the one case with consent legs
  const mine = !!word && !word.owner && !activeGroup
  useEffect(() => {
    if (!open || !word) return
    setError(''); setCopied({}); setMembers(null)
    // A VERB THE APP NEVER ASKED FOR IS NOT YOUR FAULT (owner report 2026-09-19,
    // "the realms verb is missing from your grant"): the platform answers 403
    // when the dictionary's own registration does not request a discovery
    // verb, and re-authorizing cannot add one. That is a fact about the
    // section, said in the section — not an error over the whole sheet.
    const get = (path, set, denied) => fetch(path, { credentials: 'same-origin' })
      .then(r => r.json().then(j => r.ok ? j : Promise.reject(Object.assign(new Error(j.error || 'refused'), { status: r.status }))))
      .then(set).catch(e => (e.status === 403 && denied ? denied() : setError(e.message)))
    if (mine) {
      get('/api/friends', j => setFriends(j.friends || []), () => setFriends('denied'))
      get('/api/realms', j => setRealms(j.realms || []), () => setRealms('denied'))
      get(`/api/words/${encodeURIComponent(word.id)}/audience`, setAudience)
    }
    if (activeGroup) api.members(activeGroup.id).then(j => setMembers(j.members || [])).catch(() => setMembers([]))
  }, [open, word, mine, activeGroup && activeGroup.id]) // eslint-disable-line react-hooks/exhaustive-deps

  /* the Modal stays MOUNTED and is told `open` (bug 2026-09-19: tearing the
     Radix dialog out mid-dismiss left its body pointer lock behind and the
     app "froze" — see History in WordSheet) */
  const legacy = word?.legacy
  const q = extra => `node_id=${encodeURIComponent(word.id)}&slug=${encodeURIComponent(word.slug)}&${extra}`
  // the registry entry for a person, and whether the platform says the word
  // reaches them — a consent page can say yes and the grant still leave the
  // word out, and the row must not say "shared" over that
  const entryFor = id => (audience?.shared_with || []).find(s => s.user_id === id)
  const already = id => !!entryFor(id)
  const reaching = id => { const e = entryFor(id); return e ? e.reaches !== false : null }
  const inRealm = id => (audience?.shared_realms || []).some(r => r.realm_id === id)
  // the groups a copy can go to: every one that has let the dictionary in,
  // except the one this word already stands in
  const targets = groups.filter(g => g.authorized !== false && !(activeGroup && g.id === activeGroup.id))
  const copy = async (into, label) => {
    setCopied(c => ({ ...c, [into]: 'copying' }))
    const { outcome } = await onCopy(into, label)
    setCopied(c => ({ ...c, [into]: outcome }))
  }
  const state = (into, idle) => ({ copying: '…', copied: 'copied', there: 'already there', failed: 'could not' }[copied[into]] || idle)
  const ownerName = word?.owner ? (word.owner.display_name || word.owner.username || 'a friend') : ''

  return <Modal open={!!(open && word)} onClose={onClose} label={`Share ${word?.name || ''}`} className="dt-share">
    {word && <>
    <header className="dt-share__head" {...qh('Share this word', 'Two kinds of grant: one person, who can read or also edit; or a whole circle, read-only. Either way the platform’s own consent page confirms it — this app never handles a credential.')}>
      <h2 className="dt-share__title">Share <em>{word.name}</em></h2>
      <p className="dt-share__line">
        {activeGroup ? <>Everyone in <strong>{activeGroup.name}</strong> reads this word — it is theirs together. A copy of it can go into a dictionary of yours.</>
          : word.owner ? <>{ownerName}'s word — theirs to share to people. A copy of it can go into one of your groups.</>
          : <>Whoever you share it with reads this word — the definition, its tags — from their own dictionary.</>}
      </p>
    </header>

    {legacy
      ? <p className="dt-share__note">This word is still inside an old record. Edit it once and it becomes a page of its own — then it can be shared.</p>
      : <>
        {error && <p className="dt-share__err" role="alert">{error}</p>}

        {mine && <section className="dt-share__sec" {...qh('One person', 'People you follow who follow you back. Choose read or edit, then pick a person — you are taken to the consent page to confirm.')}>
          <div className="dt-share__sechead">
            <h3>One person</h3>
            <div className="dt-share__access" {...qh('What they may do', '“Can read” shows them the word and its tags from their own dictionary. “Can edit” lets them change the definition too.')}>
              {['view', 'edit'].map(a => <button key={a} type="button" className="dt-share__verb" data-on={access === a || undefined}
                onClick={() => setAccess(a)}>{a === 'view' ? 'can read' : 'can edit'}</button>)}
            </div>
          </div>
          {friends === null ? <p className="dt-share__wait">…</p>
            : friends === 'denied' ? <p className="dt-share__none">The dictionary has not asked to see your friends yet — a verb for its registration, not for you.</p>
            : !friends.length ? <p className="dt-share__none">No mutual follows yet.</p>
            : friends.map(f => <a key={f.user_id} className="dt-share__row" href={`/share/friend?${q(`user_id=${encodeURIComponent(f.user_id)}&access=${access}`)}`}
                {...qh(f.display_name || f.username || 'A person', reaching(f.user_id) === false
                  ? 'Shared, but the platform is not showing them this word: the grant it made left the word out. Click to share again; if that does not take, fix the share on app.ofself.ai.'
                  : already(f.user_id) ? 'Already shared with them — the platform confirms they can read it. Click to share again or change what they may do.'
                  : `Share this word with them — ${access === 'edit' ? 'read and edit' : 'read-only'}. The consent page confirms before anything is granted.`)}>
                <span className="dt-share__who">{f.display_name}{f.username && <span className="dt-share__at">@{f.username}</span>}</span>
                <span className={`dt-share__state${reaching(f.user_id) === false ? ' is-amiss' : ''}`}>{reaching(f.user_id) === false ? 'shared · not reaching them' : already(f.user_id) ? 'shared' : 'share →'}</span>
              </a>)}
        </section>}

        {mine && <section className="dt-share__sec" {...qh('A circle', 'Your privacy circles. Sharing to one opens the word, read-only, to everyone in it — including people who join later.')}>
          <h3>A circle</h3>
          <p className="dt-share__sub">Read-only, to everyone in it.</p>
          {realms === null ? <p className="dt-share__wait">…</p>
            : realms === 'denied' ? <p className="dt-share__none">The dictionary has not asked to see your circles yet — a verb for its registration, not for you.</p>
            : !realms.length ? <p className="dt-share__none">No circles yet.</p>
            : realms.map(r => <a key={r.id} className="dt-share__row" href={`/share/realm?${q(`realm_id=${encodeURIComponent(r.id)}`)}`}
                {...qh(r.name, inRealm(r.id) ? 'Already open to this circle.' : 'Open this word, read-only, to everyone in this circle. The consent page confirms first.')}>
                <span className="dt-share__who">{r.name}</span>
                <span className="dt-share__state">{inRealm(r.id) ? 'shared' : 'share →'}</span>
              </a>)}
        </section>}

        {/* the group's members: who reads it — each wears a face, and the
            row says so rather than offering a verb there is none of */}
        {activeGroup && <section className="dt-share__sec" {...qh('Who reads it', 'A group’s dictionary is its members’ together: every member already reads this word. There is no sharing it to one of them — they copy it into their own dictionary, as you can below.')}>
          <h3>In {activeGroup.name}</h3>
          <p className="dt-share__sub">Every member reads it. To have it in their own dictionary, they copy it — as you can, below.</p>
          {members === null ? <p className="dt-share__wait">…</p>
            : !members.length ? <p className="dt-share__none">The group has not let the dictionary see its members.</p>
            : members.map(m => <div key={m.user_id} className="dt-share__row dt-share__row--still">
                <span className="dt-share__who"><Avatar name={m.display_name || m.username || 'member'} size="sm" /> {m.display_name}{m.username && <span className="dt-share__at">@{m.username}</span>}</span>
                <span className="dt-share__state">{m.role === 'admin' ? 'admin · reads it' : 'reads it'}</span>
              </div>)}
        </section>}

        {activeGroup && <section className="dt-share__sec" {...qh('Copy into my dictionary', 'Mints a copy of this word in your own dictionary, with a note in its history saying it came from this group.')}>
          <h3>Your dictionary</h3>
          <button type="button" className="dt-share__row dt-share__row--act" disabled={!!copied.me} onClick={() => copy('me', 'your dictionary')}>
            <span className="dt-share__who">Copy into my dictionary</span>
            <span className="dt-share__state">{state('me', 'copy →')}</span>
          </button>
        </section>}

        {/* A COPY INTO A GROUP (owner call 2026-09-19: "share to any of their
            groups"): a group's dictionary is its own graph, so the word is
            minted there — the same word, now the group's, in the group's terms */}
        <section className="dt-share__sec" {...qh('Your groups', 'A group’s dictionary is its own, so a word goes there as a copy: pressing a group mints the word in that group’s dictionary, with a note in its history saying who brought it and from where. Only groups that have let the dictionary in are listed.')}>
          <h3>{activeGroup ? 'Another group' : 'Your groups'}</h3>
          <p className="dt-share__sub">A copy, into the group's own dictionary.</p>
          {!targets.length ? <p className="dt-share__none">{groups.length ? 'No other group has let the dictionary in yet.' : 'No groups yet.'}</p>
            : targets.map(g => <button key={g.id} type="button" className="dt-share__row dt-share__row--act" disabled={!!copied[g.id]} onClick={() => copy(g.id, g.name)}>
                <span className="dt-share__who">{g.name}</span>
                <span className="dt-share__state">{state(g.id, 'copy →')}</span>
              </button>)}
        </section>
      </>}
    </>}
  </Modal>
}
